import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import OneliveMypageSectionHead from './OneliveMypageSectionHead';
import OnelivePcMypageShell from './OnelivePcMypageShell';
import { oneliveAssets } from './assets';
import {
  fetchCurrentMemberProfile,
  getCurrentUser,
  navigate,
  parseApiRecord,
  resolveRealName,
  showOneliveToast,
  syncCurrentUser,
  type OneliveSessionUser,
} from './api';

type ProfileForm = {
  name?: string;
  nickname?: string;
  email?: string;
  mobile?: string;
  phone?: string;
  real_name?: string;
  gender?: 'M' | 'F';
  password?: string;
  passwordConfirm?: string;
};

const DEFAULT_AVATAR = '/img/no_profile.svg';

function resolveGender(data: OneliveSessionUser | null | undefined): 'M' | 'F' {
  const raw = data?.gender || data?.member_profile?.gender || '';
  return raw.toUpperCase() === 'F' ? 'F' : 'M';
}

export default function OneliveMobileProfileEditPage() {
  const [form, setForm] = useState<ProfileForm>({});
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const user = getCurrentUser();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const profile = await fetchCurrentMemberProfile();
        const data = profile as OneliveSessionUser | null;
        if (mounted) {
          setForm({
            name: data?.name || user?.name || '',
            nickname: data?.nickname || user?.nickname || '',
            email: data?.email || user?.email || '',
            mobile: data?.mobile || user?.mobile || '',
            phone: data?.phone || user?.phone || '',
            real_name: resolveRealName(data) || resolveRealName(user),
            gender: resolveGender(data ?? user),
          });
          setAvatarUrl(
            data?.avatar
            || data?.member_profile?.avatar_url
            || user?.avatar
            || DEFAULT_AVATAR,
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user?.email, user?.mobile, user?.name, user?.nickname, user?.avatar]);

  const onAvatarChange = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showOneliveToast('이미지 파일만 업로드할 수 있습니다.', 'warning');
      ev.target.value = '';
      return;
    }

    setUploadingAvatar(true);
    try {
      const api = (window as { G7Core?: { api?: { post: (url: string, body: FormData) => Promise<unknown> } } }).G7Core?.api;
      if (!api) throw new Error('api');

      const body = new FormData();
      body.append('avatar', file);
      const res = await api.post('/me/avatar', body);
      const data = parseApiRecord<{ avatar?: string }>(res);
      const nextAvatar = data?.avatar || URL.createObjectURL(file);
      setAvatarUrl(nextAvatar);

      const current = getCurrentUser();
      if (current?.uuid) {
        (window as { G7Core?: { state?: { set?: (payload: Record<string, unknown>, opts: { target: string }) => void } } }).G7Core?.state?.set?.(
          { currentUser: { ...current, avatar: nextAvatar } },
          { target: 'global' },
        );
      }
      showOneliveToast('회원이미지가 변경되었습니다.', 'success');
    } catch {
      showOneliveToast('이미지 업로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setUploadingAvatar(false);
      ev.target.value = '';
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (form.password && form.password !== form.passwordConfirm) {
      showOneliveToast('비밀번호가 일치하지 않습니다.', 'error');
      return;
    }

    if (!form.real_name?.trim()) {
      showOneliveToast('이름을 입력해 주세요.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const api = (window as { G7Core?: { api?: { put: (url: string, body: Record<string, string>) => Promise<unknown> } } }).G7Core?.api;
      if (!api) throw new Error('api');

      const body: Record<string, string> = {
        name: form.name || '',
        email: form.email || '',
        mobile: form.mobile?.trim() || '',
        phone: form.phone?.trim() || '',
        gender: form.gender || 'M',
      };
      if (form.real_name?.trim()) {
        body.real_name = form.real_name.trim();
      }
      if (form.password) {
        body.password = form.password;
        body.password_confirmation = form.passwordConfirm || '';
      }

      const res = await api.put('/me', body);
      const updated = parseApiRecord<OneliveSessionUser>(res);
      if (updated?.uuid) {
        syncCurrentUser(updated);
      }
      showOneliveToast('회원정보가 수정되었습니다.', 'success');
      navigate('/mypage/profile');
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || '회원정보 수정 중 오류가 발생했습니다.';
      showOneliveToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnelivePcMypageShell pageClassName="onelive-profile-edit-page">
      <OneliveMypageSectionHead title="회원정보수정" iconSrc={oneliveAssets.drawerMypage}>
        {loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : (
          <form className="onelive-mypage-edit-form" onSubmit={onSubmit}>
            <section className="onelive-mypage-form-card">
              <h3 className="onelive-mypage-form-card-title">사이트 이용정보</h3>
              <div className="onelive-mypage-form-grid">
                <div className="onelive-mypage-field onelive-mypage-field--readonly">
                  <span className="onelive-mypage-field-label">아이디</span>
                  <strong className="onelive-mypage-readonly-value">{form.name || '-'}</strong>
                  <span className="onelive-mypage-field-hint">회원아이디는 변경할 수 없습니다.</span>
                </div>
                <label className="onelive-mypage-field">
                  <span className="onelive-mypage-field-label">비밀번호</span>
                  <input
                    type="password"
                    className="onelive-mypage-input"
                    placeholder="변경 시에만 입력"
                    value={form.password || ''}
                    onChange={(ev) => setForm((prev) => ({ ...prev, password: ev.target.value }))}
                  />
                </label>
                <label className="onelive-mypage-field">
                  <span className="onelive-mypage-field-label">비밀번호 확인</span>
                  <input
                    type="password"
                    className="onelive-mypage-input"
                    placeholder="비밀번호 확인"
                    value={form.passwordConfirm || ''}
                    onChange={(ev) => setForm((prev) => ({ ...prev, passwordConfirm: ev.target.value }))}
                  />
                </label>
              </div>
            </section>

            <section className="onelive-mypage-form-card">
              <h3 className="onelive-mypage-form-card-title">개인정보</h3>
              <div className="onelive-mypage-form-grid">
                <div className="onelive-mypage-field onelive-mypage-field--readonly">
                  <span className="onelive-mypage-field-label">이름</span>
                  {form.real_name ? (
                    <strong className="onelive-mypage-readonly-value">{form.real_name}</strong>
                  ) : (
                    <input
                      type="text"
                      className="onelive-mypage-input"
                      value={form.real_name || ''}
                      onChange={(ev) => setForm((prev) => ({ ...prev, real_name: ev.target.value }))}
                      placeholder="이름"
                    />
                  )}
                </div>
                <div className="onelive-mypage-field onelive-mypage-field--readonly">
                  <span className="onelive-mypage-field-label">닉네임</span>
                  <strong className="onelive-mypage-readonly-value">{form.nickname || '-'}</strong>
                </div>
                <fieldset className="onelive-mypage-field onelive-mypage-field--gender">
                  <legend className="onelive-mypage-field-label">성별</legend>
                  <div className="onelive-mypage-gender-group">
                    <label className="onelive-mypage-gender-option">
                      <input
                        type="radio"
                        name="mb_sex"
                        value="M"
                        checked={form.gender !== 'F'}
                        onChange={() => setForm((prev) => ({ ...prev, gender: 'M' }))}
                      />
                      <span>남자</span>
                    </label>
                    <label className="onelive-mypage-gender-option">
                      <input
                        type="radio"
                        name="mb_sex"
                        value="F"
                        checked={form.gender === 'F'}
                        onChange={() => setForm((prev) => ({ ...prev, gender: 'F' }))}
                      />
                      <span>여자</span>
                    </label>
                  </div>
                </fieldset>
                <label className="onelive-mypage-field">
                  <span className="onelive-mypage-field-label">휴대폰번호</span>
                  <input
                    type="text"
                    className="onelive-mypage-input"
                    value={form.mobile || ''}
                    onChange={(ev) => setForm((prev) => ({ ...prev, mobile: ev.target.value }))}
                    placeholder="휴대폰번호"
                  />
                </label>
              </div>
            </section>

            <section className="onelive-mypage-form-card">
              <h3 className="onelive-mypage-form-card-title">회원이미지</h3>
              <div className="onelive-mypage-avatar-row">
                <div className="onelive-mypage-avatar-preview">
                  <img src={avatarUrl} alt="회원이미지 미리보기" width={72} height={72} />
                </div>
                <div className="onelive-mypage-avatar-actions">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    id="reg_mb_img"
                    accept="image/*"
                    onChange={onAvatarChange}
                    disabled={uploadingAvatar}
                    hidden
                  />
                  <button
                    type="button"
                    className="onelive-mypage-btn-secondary"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? '업로드 중...' : '이미지 변경'}
                  </button>
                  <p className="onelive-mypage-field-hint">gif, jpg, png · 60×60px 이하 권장</p>
                </div>
              </div>
            </section>

            <div className="onelive-mypage-form-actions is-split">
              <button type="button" className="onelive-mypage-btn-secondary" onClick={() => navigate('/mypage/profile')}>
                취소
              </button>
              <button type="submit" className="onelive-mypage-btn-primary" disabled={submitting || uploadingAvatar}>
                {submitting ? '저장 중...' : '정보수정'}
              </button>
            </div>
          </form>
        )}
      </OneliveMypageSectionHead>
    </OnelivePcMypageShell>
  );
}
